import { LOYALTY_ENABLED, charsPerLine, EscPos, formatMoney, formatQty, inclusiveTax, lineGross, lineNet, pointsFor, type Bill, type Shift } from '@sense/shared';
import { db } from './db';
import { isNativeApp, nativeMessage, SdDevices } from './native';
import { directPrinter, sendToPrinter, usePrinter } from './printer';
import type { Me } from './session';
import { toast } from './toast';

export function paperWidth(): 58 | 80 {
  return localStorage.getItem('sd.paper') === '58' ? 58 : 80;
}

export const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export async function receiptHtml(bill: Bill, me: Me): Promise<string> {
  const products = await db.products.bulkGet(bill.items.map((l) => l[0]));
  const w = paperWidth();
  const gst = new Map<number, number>();
  const rows = bill.items.map((l, i) => {
    const p = products[i];
    const net = lineNet(l);
    gst.set(l[4], (gst.get(l[4]) ?? 0) + net);
    return `<tr><td colspan="3">${esc(p?.name ?? `#${l[0]}`)}</td></tr>
      <tr><td>${formatQty(l[1], p?.unit ?? 'pc')} × ${formatMoney(l[2], { symbol: false })}</td><td></td><td class="r">${formatMoney(lineGross(l), { symbol: false })}</td></tr>`;
  }).join('');
  const gstRows = [...gst.entries()].filter(([bp]) => bp > 0).map(([bp, amt]) => {
    const tax = inclusiveTax(amt, bp);
    return `<tr><td>GST ${bp / 100}% (CGST+SGST)</td><td></td><td class="r">${formatMoney(tax, { symbol: false })}</td></tr>`;
  }).join('');
  const cust = bill.customer_id ? await db.customers.get(bill.customer_id) : undefined;
  const earned = bill.customer_id && bill.kind === 0 ? pointsFor(bill.total) : 0;
  const offerRows = bill.offers?.length ? await db.offers.bulkGet(bill.offers.map(([id]) => id)) : [];
  const saved = (bill.offers ?? []).reduce((a, [, amt]) => a + amt, 0);
  const offersHtml = (bill.offers ?? []).map(([id, amt], i) =>
    `<tr><td>${esc(offerRows[i]?.name ?? `Offer #${id}`)}</td><td></td><td class="r">−${formatMoney(amt, { symbol: false })}</td></tr>`).join('');
  const pays = bill.payments.map(([m, a]) => `<tr><td>${m.toUpperCase()}</td><td></td><td class="r">${formatMoney(a, { symbol: false })}</td></tr>`).join('');
  const s = me.store;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(bill.bill_no)}</title><style>
    @page { size: ${w}mm auto; margin: 0 }
    body { width: ${w - 6}mm; margin: 3mm; font: 12px/1.35 'IBM Plex Mono', ui-monospace, monospace; color: #000 }
    h1 { font-size: 15px; text-align: center; margin: 0 }
    .c { text-align: center } .r { text-align: right } .m { color: #333 }
    table { width: 100%; border-collapse: collapse } hr { border: 0; border-top: 1px dashed #000; margin: 6px 0 }
    .t td { font-weight: 700; font-size: 14px }
  </style></head><body>
    <h1>${esc(s.name)}</h1>
    ${s.address ? `<div class="c">${esc(s.address)}</div>` : ''}
    ${s.phone ? `<div class="c">Ph ${esc(s.phone)}</div>` : ''}
    ${s.gstin ? `<div class="c">GSTIN ${esc(s.gstin)}</div>` : ''}
    <hr><div>${bill.kind === 1 ? 'RETURN ' : ''}Bill ${esc(bill.bill_no)}</div>
    <div>${new Date(bill.ts).toLocaleString('en-IN')} · ${esc(bill.cashier)}</div>
    ${cust ? `<div>Customer ${esc(cust.name)} · ${esc(cust.phone)}</div>` : bill.customer ? `<div>Customer ${esc(bill.customer)}</div>` : ''}
    <hr><table>${rows}</table><hr>
    <table>
      <tr><td>Items</td><td></td><td class="r">${bill.items.length}</td></tr>
      <tr><td>Subtotal</td><td></td><td class="r">${formatMoney(bill.subtotal, { symbol: false })}</td></tr>
      ${offersHtml}
      ${bill.discount - saved > 0 ? `<tr><td>Discount</td><td></td><td class="r">−${formatMoney(bill.discount - saved, { symbol: false })}</td></tr>` : ''}
      ${bill.round ? `<tr><td>Round off</td><td></td><td class="r">${formatMoney(bill.round, { symbol: false })}</td></tr>` : ''}
      <tr class="t"><td>TOTAL</td><td></td><td class="r">${formatMoney(bill.total)}</td></tr>
      ${gstRows}
    </table><hr><table>${pays}</table><hr>
    ${saved > 0 ? `<div class="c" style="font-weight:700">You saved ${formatMoney(saved)} today!</div>` : ''}
    ${LOYALTY_ENABLED && cust ? `<div class="c">Points earned ${earned} · total ${cust.points}</div>` : ''}
    ${cust && cust.balance > 0 ? `<div class="c" style="font-weight:700">Khata balance due ${formatMoney(cust.balance)}</div>` : ''}
    <div class="c">${esc(s.settings.receiptFooter || 'Thank you! Visit again.')}</div>
  </body></html>`;
}

/** Z report: end-of-shift summary for the cash drawer. */
export function shiftHtml(sh: Shift, me: Me, kind: 'X' | 'Z' = 'Z'): string {
  const w = paperWidth();
  const row = (l: string, v: string, bold = false) => `<tr${bold ? ' class="t"' : ''}><td>${esc(l)}</td><td class="r">${v}</td></tr>`;
  const t = sh.totals;
  const diff = (sh.counted ?? 0) - (sh.expected ?? 0);
  const fmt = (p: number) => formatMoney(p, { symbol: false });
  return `<!doctype html><html><head><meta charset="utf-8"><title>${kind} report</title><style>
    @page { size: ${w}mm auto; margin: 0 } body { width: ${w - 6}mm; margin: 3mm; font: 12px/1.4 'IBM Plex Mono', monospace }
    h1 { font-size: 15px; text-align: center; margin: 0 } .c { text-align: center } .r { text-align: right }
    table { width: 100%; border-collapse: collapse } hr { border: 0; border-top: 1px dashed #000; margin: 6px 0 } .t td { font-weight: 700 }
  </style></head><body>
    <h1>${esc(me.store.name)}</h1><div class="c">${kind} REPORT · ${esc(me.device.code)}${kind === 'X' ? ' · shift still open' : ''}</div><hr>
    <table>
      ${row('Opened', new Date(sh.opened_at).toLocaleString('en-IN'))}${row('By', esc(sh.opened_by))}
      ${row('Closed', sh.closed_at ? new Date(sh.closed_at).toLocaleString('en-IN') : '—')}${row('By', esc(sh.closed_by ?? '—'))}
    </table><hr><table>
      ${row('Bills', String(t?.bills ?? 0))}${row('Sales', fmt(t?.sales ?? 0))}${row('Returns', fmt(t?.returns ?? 0))}
      ${Object.entries(t?.byMethod ?? {}).map(([m, v]) => row(m.toUpperCase(), fmt(v))).join('')}
    </table><hr><table>
      ${row('Opening float', fmt(sh.float))}
      ${sh.moves.map(([, a, why]) => row(why, (a < 0 ? '-' : '+') + fmt(Math.abs(a)))).join('')}
      ${row('Expected cash', fmt(sh.expected ?? 0), true)}${kind === 'Z' ? `${row('Counted cash', fmt(sh.counted ?? 0), true)}
      ${row(diff === 0 ? 'Tallied' : diff < 0 ? 'SHORT' : 'EXCESS', fmt(Math.abs(diff)), true)}` : ''}
    </table><hr><table>
      ${Object.entries(sh.denominations ?? {}).map(([d, n]) => row(`₹${d} × ${n}`, fmt(Number(d) * 100 * n))).join('')}
    </table><hr><div class="c">Printed ${new Date().toLocaleString('en-IN')}</div>
  </body></html>`;
}

/** Print an HTML document: the Android print service in the app, a hidden iframe in browsers (the till page never navigates away). */
export async function printHtml(html: string) {
  if (isNativeApp()) {
    const name = /<title>([^<]*)<\/title>/.exec(html)?.[1] ?? 'Sense Daily';
    return SdDevices.printHtml({ html, name }).catch((e) => toast.error(nativeMessage(e)));
  }
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();
  await new Promise((r) => setTimeout(r, 150));
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  // Give the keyboard back to the till at once, so the next scan isn't typed into the print frame.
  window.focus();
  (document.querySelector<HTMLInputElement>('input[aria-label^="Scan or search"], input[enterkeyhint="search"]') ?? document.body).focus();
  setTimeout(() => frame.remove(), 2000);
}

/** ESC/POS receipt for directly connected thermal printers (same content as receiptHtml). */
export async function receiptEscPos(bill: Bill, me: Me, opts: { drawer?: boolean } = {}): Promise<Uint8Array> {
  const products = await db.products.bulkGet(bill.items.map((l) => l[0]));
  const cust = bill.customer_id ? await db.customers.get(bill.customer_id) : undefined;
  const offerRows = bill.offers?.length ? await db.offers.bulkGet(bill.offers.map(([id]) => id)) : [];
  const saved = (bill.offers ?? []).reduce((a, [, amt]) => a + amt, 0);
  const w = charsPerLine(paperWidth());
  const m = (p: number) => formatMoney(p, { symbol: false });
  const s = me.store;
  const r = new EscPos(w);
  if (opts.drawer) r.drawer();
  r.align('center').bold(true).big(true).text(s.name, w / 2).big(false).bold(false);
  if (s.address) r.text(s.address);
  if (s.phone) r.text(`Ph ${s.phone}`);
  if (s.gstin) r.text(`GSTIN ${s.gstin}`);
  r.align('left').rule();
  r.pair(`${bill.kind === 1 ? 'RETURN ' : ''}Bill ${bill.bill_no}`, '');
  r.pair(new Date(bill.ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }), bill.cashier);
  if (cust) r.text(`Customer ${cust.name} ${cust.phone}`);
  else if (bill.customer) r.text(`Customer ${bill.customer}`);
  r.rule();
  const gst = new Map<number, number>();
  bill.items.forEach((l, i) => {
    const p = products[i];
    gst.set(l[4], (gst.get(l[4]) ?? 0) + lineNet(l));
    r.text(p?.name ?? `#${l[0]}`);
    r.pair(`  ${formatQty(l[1], p?.unit ?? 'pc')} x ${m(l[2])}`, m(lineGross(l)));
  });
  r.rule();
  r.pair('Items', String(bill.items.length));
  r.pair('Subtotal', m(bill.subtotal));
  (bill.offers ?? []).forEach(([id, amt], i) => r.pair(offerRows[i]?.name ?? `Offer #${id}`, `-${m(amt)}`));
  if (bill.discount - saved > 0) r.pair('Discount', `-${m(bill.discount - saved)}`);
  if (bill.round) r.pair('Round off', m(bill.round));
  r.bold(true).big(true).pair('TOTAL', formatMoney(bill.total), w / 2).big(false).bold(false);
  for (const [bp, amt] of gst) if (bp > 0) r.pair(`GST ${bp / 100}% (CGST+SGST)`, m(inclusiveTax(amt, bp)));
  r.rule();
  for (const [method, amt] of bill.payments) r.pair(method === 'credit' ? 'KHATA' : method.toUpperCase(), m(amt));
  r.rule().align('center');
  if (saved > 0) r.bold(true).text(`You saved ${formatMoney(saved)} today!`).bold(false);
  if (LOYALTY_ENABLED && cust) r.text(`Points earned ${pointsFor(bill.total)} - total ${cust.points}`);
  if (cust && cust.balance > 0) r.bold(true).text(`Khata balance due ${formatMoney(cust.balance)}`).bold(false);
  r.text(s.settings.receiptFooter || 'Thank you! Visit again.');
  r.feed(3);
  if (usePrinter.getState().config.cut) r.cut();
  return r.bytes();
}

export function shiftEscPos(sh: Shift, me: Me, kind: 'X' | 'Z' = 'Z'): Uint8Array {
  const w = charsPerLine(paperWidth());
  const m = (p: number) => formatMoney(p, { symbol: false });
  const t = sh.totals;
  const diff = (sh.counted ?? 0) - (sh.expected ?? 0);
  const r = new EscPos(w).align('center').bold(true).text(me.store.name).bold(false)
    .text(`${kind} REPORT - ${me.device.code}${kind === 'X' ? ' - shift still open' : ''}`).align('left').rule()
    .pair('Opened', new Date(sh.opened_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })).pair('By', sh.opened_by)
    .pair('Closed', sh.closed_at ? new Date(sh.closed_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '-').pair('By', sh.closed_by ?? '-').rule()
    .pair('Bills', String(t?.bills ?? 0)).pair('Sales', m(t?.sales ?? 0)).pair('Returns', m(t?.returns ?? 0));
  for (const [k, v] of Object.entries(t?.byMethod ?? {})) r.pair(k.toUpperCase(), m(v));
  r.rule().pair('Opening float', m(sh.float));
  for (const [, a, why] of sh.moves) r.pair(why, (a < 0 ? '-' : '+') + m(Math.abs(a)));
  r.bold(true).pair('Expected cash', m(sh.expected ?? 0));
  if (kind === 'Z') r.pair('Counted cash', m(sh.counted ?? 0)).pair(diff === 0 ? 'Tallied' : diff < 0 ? 'SHORT' : 'EXCESS', m(Math.abs(diff)));
  r.bold(false).rule();
  for (const [d, n] of Object.entries(sh.denominations ?? {})) r.pair(`Rs.${d} x ${n}`, m(Number(d) * 100 * n));
  r.align('center').text(`Printed ${new Date().toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}`).feed(3);
  if (usePrinter.getState().config.cut) r.cut();
  return r.bytes();
}

/** Sample slip for Settings › Devices: checks width, characters, cutter and (optionally) the drawer. */
export function testEscPos(me: Me | null): Uint8Array {
  const w = charsPerLine(paperWidth());
  const r = new EscPos(w).align('center').bold(true).big(true).text('Sense Daily', w / 2).big(false).bold(false)
    .text('Printer test').text(me ? `${me.store.name} - ${me.device.code}` : '').align('left').rule()
    .text('0123456789'.repeat(Math.ceil(w / 10)).slice(0, w))
    .pair('Whole Wheat Atta 5 kg', '285.00').pair('  1 x 285.00', '285.00')
    .bold(true).pair('TOTAL', formatMoney(28500)).bold(false).rule()
    .align('center').text(`${w} characters per line (${paperWidth()} mm)`).text(new Date().toLocaleString('en-IN')).feed(3);
  if (usePrinter.getState().config.cut) r.cut();
  return r.bytes();
}

/** Direct printer first; if it fails, say why and offer the print dialog instead. */
async function direct(bytes: () => Promise<Uint8Array> | Uint8Array, html: () => Promise<string> | string) {
  try {
    await sendToPrinter(await bytes());
  } catch (e) {
    usePrinter.setState({ connected: false, error: (e as Error).message });
    toast.error((e as Error).message, { label: 'Print dialog', run: () => void Promise.resolve(html()).then(printHtml) });
  }
}

export async function printShift(sh: Shift, me: Me, kind: 'X' | 'Z' = 'Z') {
  if (directPrinter()) return direct(() => shiftEscPos(sh, me, kind), () => shiftHtml(sh, me, kind));
  await printHtml(shiftHtml(sh, me, kind));
}

/** Receipt to the terminal's printer. `cash` opens the drawer when the printer drives one. */
export async function printBill(bill: Bill, me: Me, opts: { cash?: boolean } = {}) {
  if (directPrinter()) {
    const drawer = !!opts.cash && usePrinter.getState().config.drawer;
    return direct(() => receiptEscPos(bill, me, { drawer }), () => receiptHtml(bill, me));
  }
  await printHtml(await receiptHtml(bill, me));
}

/** A4 document (purchase orders, reports) with the store letterhead. */
export function a4Html(title: string, me: Me, body: string): string {
  const s = me.store;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
    @page { size: A4; margin: 14mm }
    body { font: 12px/1.45 Manrope, 'Segoe UI', system-ui, sans-serif; color: #16201a }
    h1 { font-size: 20px; margin: 0 0 2px } h2 { font-size: 15px; margin: 18px 0 6px }
    .m { color: #56635b } .r { text-align: right } .n { font-family: 'IBM Plex Mono', ui-monospace, monospace }
    table { width: 100%; border-collapse: collapse; margin-top: 8px }
    th, td { padding: 6px 8px; border-bottom: 1px solid #dde3dc; text-align: left }
    th { background: #f1f6f2; font-size: 11px; text-transform: uppercase; letter-spacing: .04em }
    tfoot td { font-weight: 700; border-top: 2px solid #1b5236 }
    .head { display: flex; justify-content: space-between; border-bottom: 3px solid #1b5236; padding-bottom: 10px }
  </style></head><body>
    <div class="head"><div><h1>${esc(s.name)}</h1><div class="m">${esc([s.address, s.phone && `Ph ${s.phone}`, s.gstin && `GSTIN ${s.gstin}`].filter(Boolean).join(' · '))}</div></div>
    <div class="r"><h1>${esc(title)}</h1><div class="m">${new Date().toLocaleString('en-IN')}</div></div></div>
    ${body}
  </body></html>`;
}

/** Plain-text receipt for WhatsApp (e-receipt). */
export async function receiptText(bill: Bill, me: Me): Promise<string> {
  const products = await db.products.bulkGet(bill.items.map((l) => l[0]));
  const lines = bill.items.map((l, i) => `${products[i]?.name ?? `#${l[0]}`}\n   ${formatQty(l[1], products[i]?.unit ?? 'pc')} × ${formatMoney(l[2])} = ${formatMoney(lineNet(l))}`);
  const saved = (bill.offers ?? []).reduce((a, [, amt]) => a + amt, 0);
  const cust = bill.customer_id ? await db.customers.get(bill.customer_id) : undefined;
  return [
    `*${me.store.name}*`,
    `${bill.kind === 1 ? 'Return' : 'Bill'} ${bill.bill_no} · ${new Date(bill.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    '',
    ...lines,
    '',
    bill.discount > 0 ? `Discount: −${formatMoney(bill.discount)}` : '',
    `*Total: ${formatMoney(bill.total)}*  (GST incl. ${formatMoney(bill.tax)})`,
    `Paid: ${bill.payments.map(([m, a]) => `${m === 'credit' ? 'Khata' : m.toUpperCase()} ${formatMoney(a)}`).join(' + ')}`,
    saved > 0 ? `You saved ${formatMoney(saved)} today!` : '',
    cust && cust.balance > 0 ? `Khata balance due: ${formatMoney(cust.balance)}` : '',
    '',
    me.store.settings.receiptFooter || 'Thank you! Visit again.',
  ].filter((x, i, a) => x !== '' || a[i - 1] !== '').join('\n');
}

export async function whatsappReceipt(bill: Bill, me: Me) {
  const phone = (bill.customer ?? '').replace(/\D/g, '').slice(-10);
  const url = `https://wa.me/${phone ? `91${phone}` : ''}?text=${encodeURIComponent(await receiptText(bill, me))}`;
  window.open(url, '_blank', 'noopener');
}
